import React, {lazy, Suspense, useEffect} from "react";
import {BrowserRouter as Router, Route, Routes} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {getAuth} from "reduxStore/reducers/auth";
import {getToggleSidebar} from "reduxStore/reducers/toggleSidebar";
import Header from "navigation/Header";
import Sidebar from "navigation/Sidebar";
import Login from "routes/Login";
import {Roles} from "common/RoleFormatter";
import ProtectedRoutes from "./RouterSettings/ProtectedRoutes";
import PublicRoute from "./RouterSettings/PublicRoutes";
import image from "assets/images/preloader.svg";
import ReloadPageAfterDeployment from "./utils/reloadPageAfterDeployment";
import "utils/Formatters/formatURL";
import {CheckIsAuth} from "./redux/reducers/auth";
import {UseLazyGetMessageApi, websocketMessageTypes} from "./components/websocket/WebsocketApi";
import {UseCloseSocketSession} from "./redux/api/DisconnectSocket";
import {getCookie} from "./utils/api";
import {authConstants} from "./constants/authConstants";
import {appUrls, baseUrl} from "./constants/appUrls";
import 'zone.js';
import useFavicon from "./utils/hooks/useFavicon";

const ROUTES = [
  {
    path: appUrls.users,
    component: lazy(() => import('routes/Users')),
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD],
    exact: true,
  },
  {
    path: appUrls.demoCabinet,
    component: lazy(() => import('routes/DemoData/DemoData')),
    rights: [
      Roles.ROLE_ADMIN,
    ],
    exact: true,
  },
  {
    path: appUrls.operationClosing,
    component: lazy(() => import('routes/FinOperationClosing/FinOperationClosing')),
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
    ],
    exact: true,
  },

  {
    path: appUrls.providers,
    component: lazy(() => import('routes/Providers/Providers')),
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD],
    exact: true,
  },
  {
    path: appUrls.verifyPayLine,
    component: lazy(() => import('routes/Payers')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SUPPORT],
  },
  {
    path: appUrls.verifySalesPayLine,
    component: lazy(() => import('routes/SalesPayer/SalesPayer')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_SALE_MANAGER],
  },
  {
    path: appUrls.payersId,
    // component: <PayersInfo/>, //src/routes/PayersInfo.js
    component: lazy(() => import('routes/PayersInfo')),
    exact: true,
    rights: [Roles.ROLE_ADMIN, Roles.ROLE_FINANCIER, Roles.ROLE_ACCOUNTING, Roles.ROLE_HEAD, Roles.ROLE_SUPPORT, Roles.ROLE_TEAM_LEAD],
  },

  {
    path: appUrls.PLPostVerification,
    component: lazy(() => import('routes/PLPostModeration/PLPostModeration')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_ACCOUNTING,
    ],
  },
  {
    path: appUrls.clientList,
    // component: <Clients/>,
    component: lazy(() => import('routes/Clients/Clients')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_HEAD,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_TEAM_LEAD],
  },
  {
    path: appUrls.leadList,
    component: lazy(() => import('routes/Leads/Leads')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_SALE_MANAGER
    ],
  },
  {
    path: appUrls.partnerProgramTerms,
    component: lazy(() => import('routes/PartnershipProgram/PPClients')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_HEAD,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_TEAM_LEAD],
  },
  {
    path: appUrls.partnerProgramRequests,
    component: lazy(() => import('routes/PartnershipProgram/PPRequests')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_HEAD,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_TEAM_LEAD],

  },
  {
    path: appUrls.bonusProgramTerms,
    component: lazy(() => import('routes/BonusProgram/BonusProgramPage')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_FINANCIER
    ],

  },
  {
    path: appUrls.bonusProgramReport,
    component: lazy(() => import('routes/BonusProgram/BonusProgramPage')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_FINANCIER
    ],

  },
  {
    path: appUrls.preModeration,
    component: lazy(() => import('routes/Screening/Screening')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SALE_MANAGER,
      Roles.TECHNICAL_SPECIALIST],
  },
  {
    path: appUrls.screeningId,
    component: lazy(() => import('routes/Screening/ScreeningById')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SALE_MANAGER,
      Roles.TECHNICAL_SPECIALIST
    ],
  },
  {
    path: appUrls.facebookReg,
    component: lazy(() => import('routes/RequestFB')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_TEAM_LEAD],
  },
  {
    path: appUrls.tikTokReg,
    component: lazy(() => import('routes/TikTokRequests/RequestTikTok')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_TEAM_LEAD],
  },
  {
    path: appUrls.system,
    component: lazy(() => import('routes/System')),
    exact: true,
    rights: [Roles.ROLE_ADMIN]
  },
  {
    path: appUrls.commissionGeneral,
    component: lazy(() => import('routes/Commission/GeneralCommission')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD]
  },
  {
    path: appUrls.commissions,
    component: lazy(() => import('routes/Commission/IndividualCommission')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD]
  },

  {
    path: appUrls.adAccount,
    component: lazy(() => import('routes/AddAccounts/AddAccounts')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_TEAM_LEAD]
  },
  {
    path: appUrls.clientsActivity,
    component: lazy(() => import('routes/ClientActiveStatusPage')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_TEAM_LEAD]
  },

  {
    path: appUrls.clientExpenses,
    component: lazy(() => import('routes/ExpensesPage')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT]
  },

  {
    path: appUrls.clientPayLine,
    component: lazy(() => import('routes/PaylinePage')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT]
  },
  {
    path: appUrls.clientAccounts,
    component: lazy(() => import('routes/ClientAccountsPage/ClientAccountsPage')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT
    ]
  },
  {
    path: appUrls.clientSettings,
    component: lazy(() => import('routes/ClientSettings')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT]
  },
  {
    path: appUrls.clientInfo,
    component: lazy(() => import('routes/ClientInfo')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT]
  },
  {
    path: appUrls.CCClientsList,
    component: lazy(() => import('routes/CCClientsList/CCClientsList')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT]
  },
  {
    path: appUrls.clientFinMovement,
    component: lazy(() => import('routes/MoneyMovementPage')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT]
  },

  // {
  //   path: appUrls.clientAccounts,
  //   component: lazy(() => import('routes/ClientAccount')),
  //   exact: true,
  //   rights: [
  //     Roles.ROLE_ADMIN,
  //     Roles.ROLE_HEAD,
  //     Roles.ROLE_FINANCIER,
  //     Roles.ROLE_ACCOUNTING,
  //     Roles.ROLE_TEAM_LEAD,
  //     Roles.ROLE_SUPPORT]
  // },
  {
    path: appUrls.postPayCalculation,
    component: lazy(() => import('routes/PostPayCalculation')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_TEAM_LEAD,
    ]
  },

  {
    path: appUrls.clientFinDocs,
    component: lazy(() => import('routes/FinDoc')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT]
  },

  {
    path: appUrls.reports,
    component: lazy(() => import('routes/Reports/Reports')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD
    ]
  },
  {
    path: appUrls.exchangeRate,
    component: lazy(() => import('routes/Reports/Reports-rate')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD]
  },

  {
    path: appUrls.googleMcc,
    component: lazy(() => import('routes/MccGoogleAds/MCCGoogleAds')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_FINANCIER,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SUPPORT]
  },
  {
    path: appUrls.manualPayment,
    component: lazy(() => import('routes/ManualReplenish/ManualReplenish')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_FINANCIER
    ]
  },
  {
    path: appUrls.manualDebiting,
    component: lazy(() => import('routes/ManualDeduct/ManualDeduct')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
    ]
  },
  {
    path: appUrls.manualPenalty,
    component: lazy(() => import('routes/ManualPenalty')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD]
  },
  {
    path: appUrls.projectsAccesses,
    component: lazy(() => import('routes/Teams/Teams')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD]
  },
  {
    path: appUrls.googleTax,
    component: lazy(() => import('routes/GoogleTax/GoogleTax')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_FINANCIER
    ]
  },
  {
    path: appUrls.paymentProblems,
    component: lazy(() => import('routes/InvoicePayments/InvoicePayments')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_ACCOUNTING,
      Roles.ROLE_SALE_MANAGER]
  },
  {
    path: appUrls.competitionCertificate,
    component: lazy(() => import('routes/InvoicePayments/FinDocsPage')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD,
      Roles.ROLE_TEAM_LEAD,
      Roles.ROLE_SUPPORT,
      Roles.ROLE_ACCOUNTING]
  },
  {
    path: appUrls.advPartners,
    component: lazy(() => import('routes/ADVPartner/ADVPartner')),
    exact: true,
    rights: [
      Roles.ROLE_ADMIN,
      Roles.ROLE_HEAD]
  },

  // {
  //   path: appUrls.saasTenancy,
  //   component: lazy(() => import('routes/SAAS/Tenancies')),
  //   exact: true,
  //   rights: []
  // },
  //
  // {
  //   path: appUrls.tenancySettingsCompany,
  //   component: lazy(() => import('routes/SAAS/TenancyCompany')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // {
  //   path: appUrls.tenancyAdSystemsGoogleCreditLine,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyGoogleCreditLine')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // {
  //   path: appUrls.tenancyAdSystemsFacebookCreditLine,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyFacebookCreditLine')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  // {
  //   path: appUrls.tenancyAdSystemsFacebookApi,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyFacebookApi')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // {
  //   path: appUrls.tenancyAdSystemsTikTokCreditLine,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyTikTokCreditLine')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  // {
  //   path: appUrls.tenancyAdSystemsTikTokApi,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyTikTokApi')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  // {
  //   path: appUrls.tenancyAdSystemsYandexCreditLine,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyYandexCreditLine')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  // {
  //   path: appUrls.tenancyAdSystemsYandexApi,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyYandexApi')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // {
  //   path: appUrls.tenancyAdSystemsApiGate,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemsTenancyApiGate')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // {
  //   path: appUrls.tenancyAdSystemsGoogleClientTag,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyGoogleClientTag')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // {
  //   path: appUrls.tenancyAdSystemsGoogleApi,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemTenancyGoogleApi')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // //  AdSystemTenancyGoogleApi tenancyAdSystemsGoogleApi
  // {
  //   path: appUrls.tenancyAdSystemsServiceClientTag,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemsTenancy')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  // {
  //   path: appUrls.tenancyAdSystemsServiceApi,
  //   component: lazy(() => import('routes/SAAS/AdSystems/AdSystemsTenancy')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // //
  // {
  //   path: appUrls.tenancySettingsLanguage,
  //   component: lazy(() => import('routes/SAAS/TenancyLanguage')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // {
  //   path: appUrls.addTenancy,
  //   component: lazy(() => import('routes/SAAS/AddTenancyPage')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },
  //
  // {
  //   path: appUrls.saasGoogleAds,
  //   component: lazy(() => import('routes/SAAS/GoogleMcc/SaaSGoogleMcc')),
  //   exact: true,
  //   rights: [Roles.ROLE_ADMIN]
  // },

  {
    path: "*",
    component: lazy(() => import('routes/EmptyPage')),
    exact: true,
    rights: [Roles.ROLE_ADMIN, Roles.ROLE_HEAD, Roles.ROLE_FINANCIER, Roles.ROLE_ACCOUNTING, Roles.ROLE_TEAM_LEAD]
  },
];

export default function App() {
  const dispatch = useDispatch();
  const {isAuth, right, authInProcess} = useSelector(getAuth);
  const {isOpen} = useSelector(getToggleSidebar);
  const [disconnectSocket] = UseCloseSocketSession();
  const token = getCookie(authConstants.accessToken);
  // const {data} = UseGetMessageApi(token, {skip: !token && !isAuth});
  const [socketConnection] = UseLazyGetMessageApi(token, {skip: !token || !isAuth});
  const currentTenant = window.location.hostname.split('.')[0]

  useFavicon(currentTenant);


  useEffect(() => {
    // document.title = currentTenant;
    dispatch(CheckIsAuth());

    //   .then(res => {
    //   console.log("res", res);
    //
    //
    // })
    // if (getCookie(authConstants.accessToken) && getCookie(authConstants.accessToken) !== "undefined") {
    //   const AccessToken = getCookie(authConstants.accessToken);
    //   // let tokens = getCookie(authConstants.accessToken) && getCookie(authConstants.accessToken).split(".");
    //   //
    // }
  }, []);

  useEffect(() => {
    token && socketConnection()
  }, [token]);


  useEffect(() => {
    const onBeforeUnload = (event) => {
      console.log("disconnect 123")
      const sessionId = sessionStorage.getItem(websocketMessageTypes.SESSION_ID);
      event.preventDefault();
      sessionId && disconnectSocket(sessionId);
      event.returnValue = true;
    };

    // window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("unload", onBeforeUnload);

    return () => {
      // window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("unload", onBeforeUnload);
    };
  }, []);

  return (
    <>
      <ReloadPageAfterDeployment/>
      <Router basename={`${baseUrl}`}>
        <Suspense fallback={
          <div className={`loadingElement w-100`}>
            <img src={image} className={"loadingImage"} alt={"Loading..."}/>
          </div>}>
          {isAuth && <Header/>}
          {isAuth && <Sidebar/>}

          <div
            style={{
              paddingTop: "80px",
              background: "#F0F0F0",
              minHeight: "100vh",
            }}
            className={`px-4 pb-3 ${
              isAuth && (isOpen ? "open_sidebar" : "close_sidebar")
            }`}
          >
            <Routes>
              <Route
                path={appUrls.login}
                element={
                  <PublicRoute isAuth={isAuth} right={right}>
                    <Login/>
                  </PublicRoute>
                }
              />
              <Route path="/*" element={
                <ProtectedRoutes
                  authInProcess={authInProcess}
                  isAuth={isAuth}
                  right={right}
                  ROUTES={ROUTES}
                />
              }/>
            </Routes>
          </div>
        </Suspense>
      </Router>
    </>

  );
}
